import clsx from "clsx"
import { ReactNode, useState } from "react"
import { createPortal } from "react-dom"

import useNavTopBar, { NavTopBarContext } from "./NavTopBar.context"
import styles from "./NavTopBar.module.scss"
import { NavTopBarProps } from "./NavTopBar.types"
import NavTopBarBody from "./NavTopBarBody/NavTopBarBody"
import NavTopBarBodyEnd from "./NavTopBarBodyEnd/NavTopBarBodyEnd"
import NavTopBarBodyStart from "./NavTopBarBodyStart/NavTopBarBodyStart"
import NavTopBarCTAButton from "./NavTopBarCTAButton/NavTopBarCTAButton"
import NavTopBarHeader from "./NavTopBarHeader/NavTopBarHeader"
import NavTopBarHeaderEnd from "./NavTopBarHeader/NavTopBarHeaderEnd/NavTopBarHeaderEnd"
import NavTopBarHeaderStart from "./NavTopBarHeader/NavTopBarHeaderStart/NavTopBarHeaderStart"
import NavTopBarHorizontalDivider from "./NavTopBarHorizontalDivider/NavTopBarHorizontalDivider"
import NavTopBarPageInnerTitle from "./NavTopBarPageInnerTitle/NavTopBarPageInnerTitle"
import NavTopBarPagePresence from "./NavTopBarPagePresence/NavTopBarPagePresence"
import NavTopBarPageTitle from "./NavTopBarPageTitle/NavTopBarPageTitle"
import NavTopBarSearchButton from "./NavTopBarSearchButton/NavTopBarSearchButton"
import NavTopBarTabs from "./NavTopBarTabs/NavTopBarTabs"
import NavTopBarVerticalDivider from "./NavTopBarVerticalDivider/NavTopBarVerticalDivider"
import useNavTopBarMobileTabs from "./hooks/useNavTopBarMobileTabs"

function NavTopBarProvider(props: NavTopBarProps) {
    const { children } = props

    const [topBarRef, setNavTopBarRef] = useState<HTMLDivElement | null>(null)
    const [mobileTabsRef, setMobileTabsRef] = useState<HTMLDivElement | null>(null)

    return (
        <NavTopBarContext.Provider value={{ topBarRef, setNavTopBarRef, mobileTabsRef, setMobileTabsRef }}>
            {children}
        </NavTopBarContext.Provider>
    )
}

function NavTopBar(props: NavTopBarProps) {
    const { children } = props

    const { setNavTopBarRef } = useNavTopBar()

    return (
        <div className={clsx(styles.base, "jsNavTopBar")} data-topbar-portal={true} ref={setNavTopBarRef}>
            {children}
            <NavTopBarMobileTabsSlot />
        </div>
    )
}

function NavTopBarMobileTabsSlot() {
    const { mobileTabsRef } = useNavTopBarMobileTabs()

    return <div ref={mobileTabsRef} className={styles.mobileTabs} />
}

function NavTopBarPortal(props: { children: ReactNode }) {
    const { children } = props

    const { topBarRef } = useNavTopBar()

    if (!topBarRef) {
        return null
    } else {
        return createPortal(children, topBarRef)
    }
}

const Root = NavTopBar
const Tabs = NavTopBarTabs
const PageTitle = NavTopBarPageTitle
const PageInnerTitle = NavTopBarPageInnerTitle
const SearchButton = NavTopBarSearchButton
const CTAButton = NavTopBarCTAButton
const Header = NavTopBarHeader
const HorizontalDivider = NavTopBarHorizontalDivider
const VerticalDivider = NavTopBarVerticalDivider
const Portal = NavTopBarPortal
const PagePresence = NavTopBarPagePresence
const Body = NavTopBarBody
const BodyStart = NavTopBarBodyStart
const BodyEnd = NavTopBarBodyEnd
const Provider = NavTopBarProvider

export default {
    Root,
    Tabs,
    NavTopBarHeaderStart,
    NavTopBarHeaderEnd,
    Header,
    PageTitle,
    PageInnerTitle,
    SearchButton,
    CTAButton,
    HorizontalDivider,
    VerticalDivider,
    Portal,
    PagePresence,
    Body,
    BodyStart,
    BodyEnd,
    Provider,
}
